import { RevolvingTransactionTypeEnum } from '.spec/self-care/tsTypes';
import { useQuery } from '@apollo/client';
import { __, conforms, includes, matches } from 'lodash/fp';
import T from 'prop-types';
import React from 'react';

import Box from '@skippay/core/components/Box';
import { usePageview } from '@skippay/core/components/GoogleAnalytics';
import Headline from '@skippay/core/components/Headline';
import Redirect from '@skippay/core/components/Redirect';
import SkeletonPlaceholder from '@skippay/core/components/SkeletonPlaceholder';
import { FormattedMessage, useIntl } from '@skippay/core/i18n/intl';
import { be, bm } from '@skippay/core/utils/classUtils';

import SEO from 'components/SEO';
import { APPLICATION_DETAIL, BILLINGS, BILLINGS_ALL } from 'constants/routes';
import useDetailPreview from 'hooks/useDetailPreview';
import LayoutHeader from 'layout/components/Header';
import LayoutProperties from 'layout/components/Properties';
import MyProfileQuery from 'queries/MyProfileQuery.graphql';
import ApplyDiscount from 'sections/ApplyDiscount/ApplyDiscount';
import Complaint from 'sections/Complaint/Complaint';
import PaymentConfirmation from 'sections/PaymentConfirmation/PaymentConfirmation';
import Receipt from 'sections/Receipt';
import RevolvingTransactionDetail from 'sections/RevolvingTransactionDetail';
import TransactionDetailPlaceholder from 'sections/transaction/DetailPlaceholder';
import { hasMaxiPackage } from 'utils/currentUserUtils';

import RevolvingTransactionDetailQuery from './RevolvingTransactionDetailQuery.graphql';

import './index.sass';

const MODULE_NAME = 'RevolvingTransactionDetail';

const redirectToDefApplication = conforms({
	type: includes(__, [
		RevolvingTransactionTypeEnum.DEFERRED_PAYMENT,
		RevolvingTransactionTypeEnum.DEFERRED_PAYMENT_RETURN,
	]),
});
const redirectToBillings = matches({
	type: RevolvingTransactionTypeEnum.DEFERRED_BILLING_PAYMENT_TO_NEXT_MONTH,
});

function RevolvingTransactionDetailScreen(props) {
	usePageview({ pTitle: 'revolving_transaction_detail' });

	const intl = useIntl();
	const myProfileQuery = useQuery(MyProfileQuery);
	const isMaxi = hasMaxiPackage(myProfileQuery.data?.myProfile);
	const { data: { myContractTransaction: transaction } = {}, loading } = useQuery(
		RevolvingTransactionDetailQuery,
		{
			variables: {
				transactionId: props.transactionId,
			},
		}
	);

	const [onDetailReceiptClick, , renderReceiptContent] = useDetailPreview({
		preview: Receipt,
		sheet: Receipt,
		props: {
			transactionId: props.transactionId,
		},
	});

	const [onDetailComplaintClick, , renderComplaintContent] = useDetailPreview({
		preview: Complaint,
		sheet: Complaint,
		props: {
			transactionId: props.transactionId,
		},
	});

	const [onPaymentConfirmClick, renderPaymentConfirm] = useDetailPreview({
		preview: PaymentConfirmation,
		sheet: PaymentConfirmation,
		props: {
			transactionId: props.transactionId,
			applicationId: props.transactionId,
		},
	});

	const [onApplyDiscountClick, renderApplyDiscount] = useDetailPreview({
		preview: ApplyDiscount,
		sheet: ApplyDiscount,
		props: {
			transactionId: props.transactionId,
		},
	});

	const isMobApp = !!process.env.MOB_APP_BUILD;

	return (
		<>
			<LayoutProperties
				headerBackLink
				headerLinkTo={BILLINGS}
				headerLinkToBackInHistory
				headerTitle={
					transaction?.description ||
					intl.formatMessage({
						id: 'RevolvingTransactionDetail.web.heading',
						defaultMessage: 'Detail transakce',
					})
				}
				isLoading={loading}
			/>

			<SEO
				title={intl.formatMessage({
					id: 'screens.RevolvingTransactionDetailScreen.title',
					defaultMessage: 'Detail transakce',
				})}
			/>

			{!process.env.MOB_APP_BUILD && (
				<LayoutHeader
					title={
						<FormattedMessage
							id="RevolvingTransactionDetail.web.heading"
							defaultMessage="Detail transakce"
						/>
					}
					linkBack={{ to: BILLINGS }}
				/>
			)}

			<Box
				borderRadius={['none', 'none', 'none', 'md', 'md', 'md']}
				mobileFullWidth={!process.env.MOB_APP_BUILD}
			>
				<div className={bm(MODULE_NAME, { isMobApp })}>
					{!isMobApp && (
						<div className={be(MODULE_NAME, 'header')}>
							<Headline component="h2" sm weight="medium">
								{loading ? (
									<div aria-hidden="true">
										<SkeletonPlaceholder height="25px" width="50%" />
									</div>
								) : (
									transaction?.description
								)}
							</Headline>
						</div>
					)}

					{loading ? (
						<div aria-hidden="true">
							<TransactionDetailPlaceholder />
						</div>
					) : !transaction ? (
						<Redirect to={BILLINGS_ALL} />
					) : redirectToBillings(transaction) ? (
						<Redirect to={BILLINGS} />
					) : redirectToDefApplication(transaction) ? (
						<Redirect
							to={{
								pathname: APPLICATION_DETAIL,
								params: {
									applicationId: transaction.additionalInfoApplication.applicationId,
								},
							}}
						/>
					) : (
						<>
							<RevolvingTransactionDetail
								isMaxi={isMaxi}
								transaction={transaction}
								onShowReceipt={onDetailReceiptClick}
								onShowComplaint={onDetailComplaintClick}
								onShowPaymentConfirm={onPaymentConfirmClick}
								onShowApplyDiscount={onApplyDiscountClick}
							/>
							{renderReceiptContent()}
							{renderComplaintContent()}
							{renderPaymentConfirm()}
							{renderApplyDiscount()}
						</>
					)}
				</div>
			</Box>
		</>
	);
}

RevolvingTransactionDetailScreen.propTypes = {
	transactionId: T.string.isRequired,
};

export default RevolvingTransactionDetailScreen;
